import { useCopyToClipboard } from '@/shared/hooks'
import { LoadingIndicator, Toast } from '@/shared/ui'
import { RKNStatus } from '@synapex/prisma/client'
import {
    telegramDescriptorToPeerString,
    telegramGetPeerTmeUrl,
    type TelegramPeerDescriptor,
} from '@synapex/shared2/telegram'
import clsx from 'clsx'
import { Suspense, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { useTmeInfoQuery } from '@/true-fsd/data/telegram'
import { useInView } from 'react-intersection-observer'
import './niceChannelBox.scss'

const RKN_TEXT: Record<RKNStatus, string> = {
    active: 'Есть в реестре РКН',
    not_active: 'Нет в реестре РКН',
    sub_limit_not_met: 'Наличие в реестре РКН не требуется',
}

type NiceChannelBoxProps = {
    title?: string | null
    photoUrl?: string | null
    channelId?: number
    onRemove?: () => void
    className?: string
    spanClassName?: string
    rknStatus?: RKNStatus | 'loading'
    toastClassName?: string
    username?: string | null
    invite_hash?: string | null
}

export const NiceChannelBox = ({
    title,
    username,
    invite_hash,
    photoUrl,
    onRemove,
    className,
    spanClassName,
    rknStatus,
    toastClassName,
}: NiceChannelBoxProps) => {
    const { copy, toastVisible, toastMessage, hideToast } = useCopyToClipboard()

    const tmeLink = telegramGetPeerTmeUrl({ username, invite_hash })?.href

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation()

        if (tmeLink) {
            copy(tmeLink)
        }
    }

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onRemove) {
            onRemove()
        }
    }

    const [tooltipState, setTooltipState] = useState({
        isVisible: false,
        content: '',
        position: { x: 0, y: 0 },
    })

    const isTooltipVisibleRef = useRef(false)

    const showTooltip = (content: string, event: React.MouseEvent) => {
        const rect = (event.target as HTMLElement).getBoundingClientRect()
        setTooltipState({
            isVisible: true,
            content,
            position: { x: rect.left + 8, y: rect.top - 36 }, // Position above the badge
        })
        isTooltipVisibleRef.current = true // Update the ref
    }

    const hideTooltip = () => {
        setTooltipState({
            isVisible: false,
            content: '',
            position: { x: 0, y: 0 },
        })
        isTooltipVisibleRef.current = false
    }

    const alt = title ?? 'Channel Icon'

    return (
        <>
            <div
                className={clsx('nice-channel-box', className)}
                onClick={handleCopy}
            >
                {photoUrl ? (
                    <NiceChannelIcon
                        src={photoUrl}
                        alt={alt}
                        title={title}
                        spanClassName={spanClassName}
                    />
                ) : (
                    <Suspense
                        fallback={
                            <div className="nice-channel-box__icon-container">
                                <LoadingIndicator
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        marginRight: '0.5em',
                                    }}
                                />
                            </div>
                        }
                    >
                        <QueryNiceChannelIcon
                            alt={alt}
                            username={username}
                            invite_hash={invite_hash}
                            spanClassName={spanClassName}
                        />
                    </Suspense>
                )}
                {onRemove && (
                    <button
                        type="button"
                        onClick={handleRemove}
                        className="nice-channel-box__remove"
                    >
                        ×
                    </button>
                )}
                {rknStatus &&
                    (rknStatus === 'loading' ? (
                        <div
                            className={clsx(
                                'rkn-status-badge rkn-status-badge-loading'
                            )}
                        >
                            <div className="flex justify-center">
                                <LoadingIndicator
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        marginRight: '0.5em',
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div
                            className={clsx('rkn-status-badge', rknStatus)}
                            onMouseEnter={(e) =>
                                showTooltip(RKN_TEXT[rknStatus], e)
                            }
                            onMouseLeave={hideTooltip}
                        >
                            РКН
                        </div>
                    ))}
            </div>
            {tooltipState.isVisible &&
                ReactDOM.createPortal(
                    <div
                        style={{
                            position: 'fixed',
                            top: tooltipState.position.y,
                            left: tooltipState.position.x,
                            background: '#000000',
                            color: '#ffffff',
                            padding: '7px 10px',
                            borderRadius: '5px',
                            fontSize: '10px',
                            whiteSpace: 'nowrap',
                            opacity: 0.7, // Add opacity
                            zIndex: 9999,
                            transform: 'translateX(-50%)', // Center horizontally
                        }}
                    >
                        {tooltipState.content}
                        <div
                            style={{
                                content: '',
                                position: 'absolute',
                                top: '100%', // Position at the bottom of the tooltip
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0, // No width for the triangle
                                height: 0, // No height for the triangle
                                borderLeft: '5px solid transparent', // Left side of the triangle
                                borderRight: '5px solid transparent', // Right side of the triangle
                                borderTop: '5px solid #000000', // Top side of the triangle (visible part)
                            }}
                        />
                    </div>,
                    document.body
                )}
            <Toast
                message={toastMessage}
                visible={toastVisible}
                onClose={hideToast}
                greenSwooshIcon={true}
                className={toastClassName}
            />
        </>
    )
}

const QueryNiceChannelIcon = ({
    username,
    invite_hash,
    alt,
    spanClassName,
}: {
    alt: string
} & Omit<TelegramPeerDescriptor, 'id'> &
    Pick<NiceChannelBoxProps, 'spanClassName' | 'title'>) => {
    const { ref: imgRef, inView } = useInView({
        triggerOnce: true,
        rootMargin: '500px',
    })

    const { data, isFetching } = useTmeInfoQuery(
        { username, invite_hash },
        {
            enabled: inView,
        }
    )

    return (
        <NiceChannelIcon
            isFetching={isFetching}
            imgRef={imgRef}
            src={data?.smallPhoto || ''}
            alt={alt}
            spanClassName={spanClassName}
            title={
                data?.title ??
                telegramDescriptorToPeerString({ username, invite_hash })
            }
        />
    )
}

type NiceChannelIconProps = {
    isFetching?: boolean
    imgRef?: (node?: Element | null) => void
    alt: string
    src: string
} & Pick<NiceChannelBoxProps, 'spanClassName' | 'title'>
const NiceChannelIcon = ({
    isFetching = false,
    imgRef,
    alt,
    src,
    spanClassName,
    title,
}: NiceChannelIconProps) => {
    return (
        <>
            <div className="nice-channel-box__icon-container">
                {isFetching ? (
                    <LoadingIndicator
                        style={{
                            width: '20px',
                            height: '20px',
                            marginRight: '0.5em',
                        }}
                    />
                ) : src ? (
                    <img
                        ref={imgRef}
                        src={src}
                        alt={alt}
                        className="nice-channel-box__icon"
                    />
                ) : (
                    <div
                        ref={imgRef}
                        style={{
                            fontSize: '8px',
                            textAlign: 'center',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#333',
                            color: 'white',
                            textTransform: 'uppercase',
                            letterSpacing: -0.25,
                        }}
                    >
                        NOT found
                    </div>
                )}
            </div>
            <span className={clsx('nice-channel-box__text', spanClassName)}>
                {title}
            </span>
        </>
    )
}
